import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import type { PagaebleResult, SearchAseguradoraDTO, WrapperResponse } from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import { errorMessage, INSURERS_PATH, insurerPath, isActive, NEW_INSURER_PATH, pageWindow } from '../common/insurer-common'
import type { FormMessage, InsurersLocationState } from '../common/insurer-common'

export const PAGE_SIZES = [20, 50, 100]
const SEARCH_DELAY_MS = 400
const LOAD_ERROR = 'No se pudo cargar el listado de aseguradoras.'
const DELETE_ERROR = 'No se pudo eliminar la aseguradora.'

export interface InsurerRow {
    id: number
    name: string
    code: string
    custom: boolean
    active: boolean
    pdf: boolean
    sync: boolean
}

export interface InsurerFilters {
    nombre: string
    abreviacion: string
    soloPropias: boolean
}

const emptyFilters: InsurerFilters = { nombre: '', abreviacion: '', soloPropias: false }

const toRow = (item: SearchAseguradoraDTO): InsurerRow => ({
    id: item.id,
    name: item.aseguradora || '—',
    code: item.abreviacion || '—',
    custom: item.estandar !== true,
    active: isActive(item.status),
    pdf: Boolean(item.pdf),
    sync: Boolean(item.rpa),
})

const toQuery = (filters: InsurerFilters, page: number, size: number) => {
    const params = [`size=${size}`, `page=${page}`, 'order=aseguradora asc']
    if (filters.nombre.trim()) params.push(`aseguradora[cn]=${encodeURIComponent(filters.nombre.trim())}`)
    if (filters.abreviacion.trim()) params.push(`abreviacion[cn]=${encodeURIComponent(filters.abreviacion.trim())}`)
    if (filters.soloPropias) params.push('estandar=false')
    return `?${params.join('&')}`
}

export const useInsurerListModel = () => {
    const history = useHistory()
    const location = useLocation()
    const locationState = location.state as InsurersLocationState | null
    const textTimer = useRef<ReturnType<typeof setTimeout>>(undefined)

    const [draft, setDraft] = useState<InsurerFilters>(emptyFilters)
    const [filters, setFilters] = useState<InsurerFilters>(emptyFilters)
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(PAGE_SIZES[0])
    const [reloadToken, setReloadToken] = useState(0)
    const [loading, setLoading] = useState(true)
    const [initialLoadDone, setInitialLoadDone] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [message, setMessage] = useState<FormMessage | null>(locationState?.flash ? { type: 'success', message: locationState.flash } : null)
    const [items, setItems] = useState<SearchAseguradoraDTO[]>([])
    const [totalItems, setTotalItems] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [deleteId, setDeleteId] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        if (locationState?.flash) history.replace(INSURERS_PATH)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                const payload: WrapperResponse<PagaebleResult<SearchAseguradoraDTO>> | null = await (await APIInvoke.invokeGET(`/aseguradoras${toQuery(filters, page, pageSize)}`)).json()
                if (!active) return
                if (!payload?.ok || !payload.body) throw new Error(payload?.message || LOAD_ERROR)
                setItems(payload.body.items || [])
                setTotalItems(payload.body.totalItems || 0)
                setTotalPages(payload.body.totalPages || 0)
                setInitialLoadDone(true)
            } catch (loadError) {
                if (active) setError(errorMessage(loadError, LOAD_ERROR))
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [filters, page, pageSize, reloadToken])

    useEffect(() => () => clearTimeout(textTimer.current), [])

    const applyFilters = (next: InsurerFilters) => {
        setPage(1)
        setFilters(next)
    }

    const changeText = (key: 'nombre' | 'abreviacion', value: string) => {
        const next = { ...draft, [key]: value }
        setDraft(next)
        clearTimeout(textTimer.current)
        textTimer.current = setTimeout(() => applyFilters(next), SEARCH_DELAY_MS)
    }

    const toggleSoloPropias = () => {
        const next = { ...draft, soloPropias: !draft.soloPropias }
        clearTimeout(textTimer.current)
        setDraft(next)
        applyFilters(next)
    }

    const clearFilters = () => {
        clearTimeout(textTimer.current)
        setDraft(emptyFilters)
        applyFilters(emptyFilters)
    }

    const confirmDelete = async () => {
        if (deleteId == null || deleting) return
        try {
            setDeleting(true)
            setMessage(null)
            const response: WrapperResponse<null> = await (await APIInvoke.invokeDELETE(`/aseguradoras/${deleteId}`)).json()
            if (response.ok) {
                setMessage({ type: 'success', message: 'Aseguradora eliminada correctamente' })
                if (items.length === 1 && page > 1) setPage(page - 1)
                else setReloadToken((token) => token + 1)
            } else {
                setMessage({ type: 'error', message: response.message || DELETE_ERROR })
            }
        } catch (deleteError) {
            console.error(deleteError)
            setMessage({ type: 'error', message: DELETE_ERROR })
        } finally {
            setDeleting(false)
            setDeleteId(null)
        }
    }

    const insurerToDelete = items.find((item) => item.id === deleteId)
    const rangeFrom = totalItems === 0 ? 0 : ((page - 1) * pageSize) + 1

    return {
        filters: draft,
        page,
        pageSize,
        loading,
        initialLoadDone,
        error,
        message,
        rows: items.map(toRow),
        totalItems,
        totalPages,
        pages: totalPages > 0 ? pageWindow(page, totalPages) : [],
        rangeFrom,
        rangeTo: totalItems === 0 ? 0 : rangeFrom + items.length - 1,
        newLocation: { pathname: NEW_INSURER_PATH, state: { backUrl: INSURERS_PATH } },
        changeNombre: (value: string) => changeText('nombre', value),
        changeAbreviacion: (value: string) => changeText('abreviacion', value),
        toggleSoloPropias,
        clearFilters,
        setPage,
        changePageSize: (value: number) => {
            setPageSize(Number(value))
            setPage(1)
        },
        reload: () => setReloadToken((token) => token + 1),
        editInsurer: (id: number) => history.push({ pathname: insurerPath(id), state: { backUrl: INSURERS_PATH } }),
        deleteOpen: deleteId != null,
        deleteLabel: insurerToDelete ? `${insurerToDelete.aseguradora || ''} (${insurerToDelete.abreviacion || ''})` : 'esta aseguradora',
        deleting,
        askDelete: (id: number) => setDeleteId(id),
        cancelDelete: () => setDeleteId(null),
        confirmDelete,
        dismissMessage: () => setMessage(null),
    }
}
